import React from "react";
import type { Options } from "@ui/Highcharts";
import { colors } from "@ui/Theme";
import type { PointOptionsObject } from "highcharts";

export interface FailureEvolutionHookOptions {
  projectId: string;
  minPeriod: number;
  maxPeriod: number;
  sparklineSeries: {
    id: string;
    axisId: string;
    startsAt: number;
    series: (Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions)[];
  }[];
}

const useOptions = ({ minPeriod, maxPeriod, sparklineSeries }: FailureEvolutionHookOptions) =>
  React.useMemo(() => {
    const axisYMinMax = sparklineSeries.reduce(
      (acc, { series }) => {
        const data: number[] = series.flatMap(serie =>
          (serie.data as PointOptionsObject[])
            .filter(
              (point): point is PointOptionsObject & { y: number } =>
                point.y !== undefined && point.y !== null,
            )
            .map(point => point.y),
        );

        const min = Math.min(acc.min, ...data);
        const max = Math.max(acc.max, ...data);

        return {
          min,
          max,
        };
      },
      {
        min: 0,
        max: 0,
      },
    );

    const _: {
      id: string;
      options: Options;
    }[] = sparklineSeries.map(({ id, axisId, series }) => ({
      id,
      options: {
        initial: {},
        chart: {
          type: "arearange",
          zoomType: undefined,
          marginTop: 5,
          marginBottom: 5,
          marginRight: 5,
          marginLeft: 5,
        },
        series,
        xAxis: {
          min: minPeriod,
          max: maxPeriod,
          type: "datetime",
          visible: false,
          labels: {
            enabled: false,
          },
        },
        yAxis: [
          {
            id: axisId,
            visible: true,
            labels: {
              enabled: false,
            },
            gridLineWidth: 0,
            min: axisYMinMax.min,
            max: axisYMinMax.max,
            plotLines: [
              {
                value: 0,
                color: colors.titanium300,
                width: 2,
              },
            ],
          },
        ],
        plotOptions: {
          series: {
            states: {
              hover: {
                enabled: false,
              },
            },
          },
        },
        tooltip: {
          enabled: false,
        },
        navigator: {
          enabled: false,
        },
      },
    }));
    return _;
  }, [minPeriod, maxPeriod, sparklineSeries]);
export default useOptions;
